/* ============================================================================
 * pqiTracker.js  \u2014  Silent telemetry collection for PQ Intelligence (PQI)
 *
 * Goal: Tag every measurable contractor event so the platform can later
 *       reason about win rates, margin patterns, phase outcomes, etc.
 *
 * Behavior:
 *   - Fully silent. Never throws. Never blocks the UI.
 *   - "Fire and forget" \u2014 callers don't await; failures log to console.warn only.
 *   - Writes to Supabase `public.pqi_data_points` with the current
 *     contractor's auth user id. RLS allows insert-own; if the session
 *     is missing or the table doesn't exist yet, we silently no-op.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';

const TABLE = 'pqi_data_points';

// Kept for API compatibility (AuthContext calls it on sign-out). contractor_id
// is no longer resolved client-side — the _stamp_tenant trigger (migration 041)
// stamps each row with the caller's tenant server-side, so a team member's
// events roll up to the contractor's dashboard instead of siloing per user.
export const _resetPqiContractorCache = () => {};

const baseRow = async () => ({
  // NO contractor_id here — it is stamped to _my_tenant() by the DB trigger.
  timestamp: new Date().toISOString(),
});

const writeRow = async (row) => {
  try {
    const { error } = await supabase.from(TABLE).insert(row);
    if (error && error.code !== 'PGRST116') {
      // 42P01 = table missing, 42501 = permission denied; silently swallow.
      // Anything else gets a console.warn but never bubbles to UI.
      // eslint-disable-next-line no-console
      console.warn('[pqi] insert failed:', error.message || error);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[pqi] insert exception:', e?.message || e);
  }
};

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const inferJobType = (job) => {
  const name = (job?.name || '').toLowerCase();
  if (/bath|shower|vanity/.test(name)) return 'bathroom';
  if (/kitchen/.test(name)) return 'kitchen';
  if (/basement/.test(name)) return 'basement';
  if (/deck|porch|patio/.test(name)) return 'deck';
  if (/addition|three.?season/.test(name)) return 'addition';
  if (/roof/.test(name)) return 'roofing';
  if (/exterior|sid(ing)?|paint/.test(name)) return 'exterior';
  if (/floor/.test(name)) return 'flooring';
  return 'other';
};

const phasesCompletedFor = (phaseIdx) => {
  const n = Number(phaseIdx);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(9, Math.max(0, Math.floor(n)));
};

/* ----------------------------------------------------------------------------
 * Public track functions \u2014 each is fire-and-forget.
 * ------------------------------------------------------------------------- */

/** Estimate created or saved as draft. */
export const trackEstimateCreated = async ({ estimate, jobId } = {}) => {
  const row = {
    ...(await baseRow()),
    event_type: 'estimate_created',
    job_id: jobId || estimate?.job_id || null,
    job_type: inferJobType({ name: estimate?.title || estimate?.client || '' }),
    bid_amount: safeNumber(estimate?.total),
    metadata: {
      estimate_id: estimate?.id || null,
      title: estimate?.title || null,
      status: estimate?.status || null,
      client_id: estimate?.client_id || null,
    },
  };
  return writeRow(row);
};

/** Job created (typically right after Create Job dialog submit). */
export const trackJobCreated = async ({ job } = {}) => {
  if (!job) return;
  const row = {
    ...(await baseRow()),
    event_type: 'job_created',
    job_id: job.id,
    job_type: inferJobType(job),
    bid_amount: safeNumber(job.contract),
    phases_completed: phasesCompletedFor(job.phase),
    metadata: {
      name: job.name || null,
      client_id: job.client_id || null,
      address: job.address || null,
    },
  };
  return writeRow(row);
};

/** Job phase advanced (e.g., 0 \u2192 1). */
export const trackJobPhaseAdvanced = async ({ job, fromPhase, toPhase } = {}) => {
  if (!job) return;
  const row = {
    ...(await baseRow()),
    event_type: 'job_phase_advanced',
    job_id: job.id,
    job_type: inferJobType(job),
    bid_amount: safeNumber(job.contract),
    phases_completed: phasesCompletedFor(toPhase ?? job.phase),
    metadata: {
      from_phase: fromPhase ?? null,
      to_phase: toPhase ?? null,
      name: job.name || null,
    },
  };
  return writeRow(row);
};

/** Job closed (typically marking status \u2192 'closed'). */
export const trackJobClosed = async ({ job, actualCost, totalRevenue, marginPct } = {}) => {
  if (!job) return;
  const row = {
    ...(await baseRow()),
    event_type: 'job_closed',
    job_id: job.id,
    job_type: inferJobType(job),
    bid_amount: safeNumber(totalRevenue ?? job.contract),
    actual_cost: safeNumber(actualCost),
    margin_pct: safeNumber(marginPct),
    won_job: true,
    phases_completed: phasesCompletedFor(job.phase),
    metadata: {
      name: job.name || null,
      client_id: job.client_id || null,
    },
  };
  return writeRow(row);
};

/** Material logged on a job. */
export const trackMaterialLogged = async ({ job, store, amount, category } = {}) => {
  const row = {
    ...(await baseRow()),
    event_type: 'material_logged',
    job_id: job?.id || null,
    job_type: job ? inferJobType(job) : null,
    bid_amount: safeNumber(job?.contract),
    actual_cost: safeNumber(amount),
    material_categories: category
      ? [String(category)]
      : store
      ? [String(store)]
      : null,
    metadata: {
      store: store || null,
      job_name: job?.name || null,
    },
  };
  return writeRow(row);
};

/** Invoice marked paid. */
export const trackInvoicePaid = async ({ job, invoiceId, amount } = {}) => {
  const row = {
    ...(await baseRow()),
    event_type: 'invoice_paid',
    job_id: job?.id || null,
    job_type: job ? inferJobType(job) : null,
    bid_amount: safeNumber(amount),
    metadata: {
      invoice_id: invoiceId || null,
      job_name: job?.name || null,
    },
  };
  return writeRow(row);
};

/** Estimate signed by client (i.e. job won). */
export const trackEstimateSigned = async ({
  estimateId,
  clientId,
  jobId,
  total,
  signedName,
  estimateTitle,
} = {}) => {
  const row = {
    ...(await baseRow()),
    event_type: 'estimate_signed',
    job_id: jobId || null,
    job_type: inferJobType({ name: estimateTitle || '' }),
    bid_amount: safeNumber(total),
    won_job: true,
    metadata: {
      estimate_id: estimateId || null,
      client_id: clientId || null,
      signed_name: signedName || null,
      estimate_title: estimateTitle || null,
    },
  };
  return writeRow(row);
};

/** Time entry recorded (clock out). */
export const trackTimeLogged = async ({ jobName, hours, cost, crewName } = {}) => {
  const row = {
    ...(await baseRow()),
    event_type: 'time_logged',
    labor_hours: safeNumber(hours),
    actual_cost: safeNumber(cost),
    metadata: {
      job_name: jobName || null,
      crew_name: crewName || null,
    },
  };
  return writeRow(row);
};

/** Margin snapshot — fired whenever cost or revenue context changes materially.
 *  Triggers (`trigger`): 'material_logged' | 'time_logged' | 'invoice_paid'
 *                        | 'phase_advanced' | 'job_closed'
 *  Callers pass pre-computed values; tracker just persists them so analytics
 *  can reconstruct a margin time-series without re-joining source tables. */
export const trackMarginSnapshot = async ({
  job,
  actualCost,
  totalRevenue,
  marginPct,
  phasesCompleted,
  trigger,
} = {}) => {
  if (!job) return;
  const row = {
    ...(await baseRow()),
    event_type: 'margin_snapshot',
    job_id: job.id || null,
    job_type: inferJobType(job),
    bid_amount: safeNumber(totalRevenue ?? job.contract),
    actual_cost: safeNumber(actualCost),
    margin_pct: safeNumber(marginPct),
    phases_completed: phasesCompletedFor(phasesCompleted ?? job.phase),
    metadata: {
      trigger: trigger || null,
      name: job.name || null,
      client_id: job.client_id || null,
      status: job.status || null,
    },
  };
  return writeRow(row);
};

export default {
  trackEstimateCreated,
  trackJobCreated,
  trackJobPhaseAdvanced,
  trackJobClosed,
  trackMaterialLogged,
  trackInvoicePaid,
  trackEstimateSigned,
  trackTimeLogged,
  trackMarginSnapshot,
};
